<template>
  <ContentList
    :columnCollapsed="columnCollapsed"
    v-bind="$attrs"
    :entries="notes"
    :loading="loading"
    dividerStyles="max-lg:hidden"
    @delete="open($event as INote)"
  >
    <template #title>
      <PaperIcon v-if="columnCollapsed" class="h-3 w-3" />
      <template v-else>
        <div class="capitalize max-lg:w-full max-lg:text-lg max-lg:font-bold">
          {{ t("entries.note", 9) }}
        </div>
      </template>
    </template>
    <template #action>
      <slot name="action" />
    </template>
    <template #subtitle>
      <span class="max-lg:font-semibold">
        {{
          !legalTextParagraph || !legalTextParagraph.legal_text
            ? t("to_norm")
            : t("to", [plainFn(legalTextParagraph)])
        }}
      </span>
    </template>
    <template
      v-for="(_, groupIndex) in groupedNotes"
      :key="groupIndex"
      #[`empty-${groupIndex}`]
    >
      {{ t("no_notes_for_entry") }}
    </template>
    <template
      v-for="(title, groupIndex) in titles"
      :key="groupIndex"
      #[`list-header-${groupIndex+1}`]
    >
      {{ title }}
    </template>
    <template #item="{ entry }">
      <NotesOverviewRow :note="entry" :columnCollapsed />
    </template>
  </ContentList>
</template>

<script lang="ts" setup>
import type { INote } from "~/models/IUserEntry";

defineOptions({ inheritAttrs: false });

defineProps<{
  columnCollapsed: boolean;
  loading?: boolean;
}>();

const { sortedNotes, activeParagraph: legalTextParagraph } =
  useParagraphNotes();

const { t } = useI18n({ useScope: "local" });

const { plainFn } = useFullParagraphTitle(undefined, true);

const groupedNotes = computed(() => {
  const groupedNotesMap = new Map<string, { title: string; notes: INote[] }>();

  // set initial group using current paragraph
  groupedNotesMap.set("latest", {
    title: t("to", [plainFn(legalTextParagraph.value)]),
    notes: [],
  });

  // Group notes based on title and validUntil
  sortedNotes.value.forEach((note: INote) => {
    const validUntilRaw = note.legal_text_paragraph.valid_until;

    const title = t("to", [plainFn(note.legal_text_paragraph)]); // Ensure title is clean
    const groupKey = validUntilRaw || "latest"; // Use "latest" for notes without validUntil

    // Add the note to its corresponding group
    if (!groupedNotesMap.has(groupKey)) {
      groupedNotesMap.set(groupKey, { title, notes: [] });
    }
    groupedNotesMap.get(groupKey)?.notes.push(note);
  });

  // Sort the groups: "latest" group first, then by validUntil (newest first)
  return Array.from(groupedNotesMap.values()).sort((a, b) => {
    const aValidUntil = a.notes[0]?.legal_text_paragraph.valid_until;
    const bValidUntil = b.notes[0]?.legal_text_paragraph.valid_until;
    if (!aValidUntil) return -1; // "latest" group first
    if (!bValidUntil) return 1;
    return new Date(bValidUntil).getTime() - new Date(aValidUntil).getTime(); // Descending order
  });
});

const notes = useArrayMap(groupedNotes, (group) => group.notes);
const titles = useArrayMap(
  () => groupedNotes.value.slice(1),
  (group) => group.title
);
const { open } = useEntryDeleteModal();
</script>

<i18n>
{
  "de": {
    "to": "zu {0}",
    "no_notes_for_entry": "Keine Notizen vorhanden.",
    "to_norm": "zur Norm "
  },
  "en": {
    "to": "for {0}",
    "no_notes_for_entry": "No notes available.",
    "to_norm": "for paragraph "
  }
}
</i18n>
